import { BsArrowCounterclockwise, BsArrowUpRight, BsChatText, BsCheck2, BsClipboard2Pulse } from 'react-icons/bs';
import { WorkListDrawerTab, WorklistAttachmentType, WorklistData, WorklistStatus } from 'models/worklist';
import { t } from 'i18next';
import { useAppContext, useDrawerContext } from 'context/hooks';
import { TableAction, TableActionConfig, TableColumnAction } from 'utils/models';
import { useState } from 'react';
import { containsAttachmentTypes, containsBillingReportAttachment, containsReportAttachment } from 'utils/worklist';
import { useNavigate } from 'react-router-dom';
import { useUpdateWorklistRecord } from 'hooks/api/worklist/useUpdateWorklistRecord';
import { useUpdateWorklistRecords } from 'hooks/api/worklist/useUpdateWorklistRecords';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { PagedResult } from '@extrahorizon/javascript-sdk';
import { useSearchParameters } from 'hooks/useSearchParameters';
import { MeasurementDetails } from 'pages/Main/Patients/Details/Overview/HeartRateOverview/MeasurementDetails/MeasurementDetails';
import { PatientDetailsTabs, PatientOverviewTab } from 'pages/Main/Patients/Patients.helpers';
import { queryKeys, queryParams } from 'utils/query';
import dayjs from 'dayjs';
import { formatToUnix } from 'utils/dates';
import { showUsaFeature } from 'utils/app';
import { WorklistDrawer } from '../Drawer/WorklistDrawer';

export const useActions = (): TableColumnAction<WorklistData>[] => {
  const { mutateAsync: updateRecord } = useUpdateWorklistRecord();
  const { openDrawer } = useDrawerContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { parameters: searchFilters } = useSearchParameters(queryKeys.worklistOverview);

  const [updatingRecords, setUpdatingRecords] = useState<Map<string, WorklistStatus>>(new Map());

  const removeUpdatingRecord = (id: string) =>
    setUpdatingRecords((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });

  const handleUpdateRecord = async (id: string, status: WorklistStatus) => {
    if (updatingRecords.has(id)) return;
    setUpdatingRecords((prev) => new Map(prev).set(id, status));

    const queryKey = [queryKeys.worklistOverview, searchFilters];

    try {
      await updateRecord({ id, status });
      const isAllFilter = searchFilters[queryParams.status] === WorklistStatus.All;
      queryClient.setQueryData<InfiniteData<PagedResult<WorklistData>>>(queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: isAllFilter
              ? page.data.map((item) => (item.id === id ? { ...item, status } : item))
              : page.data.filter((item) => item.id !== id),
          })),
        };
      });
    } catch {
      await queryClient.invalidateQueries({ queryKey });
    } finally {
      removeUpdatingRecord(id);
    }
  };

  const handleLink = (item: WorklistData) => {
    const link = `/patients/${item?.attachments?.patientId}/details`;
    if (containsReportAttachment({ item })) {
      if (showUsaFeature && containsBillingReportAttachment({ item })) {
        navigate(`/billing/?id=${item.attachments.patientId}&groups=${item.recipients.groupId}`);
        return;
      }

      navigate(`${link}/${PatientDetailsTabs.Reports}`);
      return;
    }
    const filters: string[] = [];
    let detailTab = PatientOverviewTab.Heartrate;

    if (item.attachments.measurementTimestamp) {
      filters.push(`${queryParams.timestamp}=${formatToUnix(dayjs(item.attachments.measurementTimestamp))}`);
    }

    if (
      containsAttachmentTypes({
        item,
        types: [WorklistAttachmentType.HeartRate, WorklistAttachmentType.HeartRhythmMeasurement],
      })
    ) {
      if (item?.attachments?.measurementId) {
        filters.push(`${queryParams.detailId}=${item.attachments.measurementId}`);
        openDrawer({
          content: <MeasurementDetails />,
          detailId: item.attachments.measurementId,
        });
      }
    } else if (containsAttachmentTypes({ item, types: [WorklistAttachmentType.BloodPressureMeasurement] })) {
      detailTab = PatientOverviewTab.Bloodpressure;
    } else if (containsAttachmentTypes({ item, types: [WorklistAttachmentType.WeightMeasurement] })) {
      detailTab = PatientOverviewTab.Weight;
    }

    const filterQuery = filters.length > 0 ? `?${filters.join('&')}` : '';
    navigate(`${link}/${PatientDetailsTabs.Overview}/${detailTab}/${filterQuery}`);
  };

  const actions: TableColumnAction<WorklistData>[] = [
    {
      icon: <BsCheck2 />,
      variant: 'filled',
      onClick: (item) => handleUpdateRecord(item.id, WorklistStatus.Read),
      visible: (item) => item.status === WorklistStatus.Unread,
      tooltip: t('acknowledge_label'),
      isLoading: (item) => updatingRecords.get(item.id) === WorklistStatus.Read,
    },
    {
      icon: <BsArrowCounterclockwise />,
      onClick: (item) => handleUpdateRecord(item.id, WorklistStatus.Unread),
      visible: (item) => item.status === WorklistStatus.Read,
      tooltip: t('unacknowledge_label'),
      isLoading: (item) => updatingRecords.get(item.id) === WorklistStatus.Unread,
    },
    {
      icon: <BsClipboard2Pulse />,
      tooltip: t('details_title'),
      onClick: (item) => openDrawer({ content: <WorklistDrawer item={item} tab={WorkListDrawerTab.Details} /> }),
    },
    {
      icon: <BsChatText />,
      indicator: (item) => (item.notes ? item.notes.length > 0 : false),
      tooltip: t('notes_label'),
      onClick: (item) => openDrawer({ content: <WorklistDrawer item={item} tab={WorkListDrawerTab.Notes} /> }),
    },
    {
      icon: <BsArrowUpRight />,
      tooltip: t('link_label'),
      onClick: handleLink,
    },
  ];

  return actions;
};

export const useTableActions = (): TableActionConfig => {
  const { mutate: updateRecords, isPending: isUpdating } = useUpdateWorklistRecords();
  const { updateTableRowIds } = useAppContext();

  const handleUpdateRecords = (ids: string[]) => {
    updateRecords({ ids, status: WorklistStatus.Read }, { onSuccess: () => updateTableRowIds?.([]) });
  };

  const tableActions: TableAction[] = [
    {
      icon: BsCheck2,
      label: t('acknowledge_selected_label'),
      onClick: (ids) => handleUpdateRecords(ids),
      isUpdating,
    },
  ];

  return { tableActions, isUpdating, isUpdatingLabel: t('acknowledge_selected_busy_label') };
};
